<script lang="ts">
  import SegmentDonut, { type DonutSeg } from '../charts/SegmentDonut.svelte';
  import type { LabelsBlock, TimingBlock } from '@api';

  // Top-of-page content summary: a post-share-by-category donut alongside the
  // best/weakest category + overwhelm read — the readable headline for "what lands".
  // `windowLabel` (e.g. "last 30 days") is appended to the header when the caller
  // windows the data — the Overview does, the Engagement page shows all-time.
  //
  // Takes the two `/v1/…/insights` blocks it renders rather than a whole analytics
  // payload: the best/worst read comes off `labels` and the Flooding Score off
  // `timing`, and naming them is what lets a page ask the server for exactly these
  // two (doc 04 §5.2).
  let {
    labels,
    timing,
    windowLabel = null,
  }: { labels: LabelsBlock; timing: TimingBlock; windowLabel?: string | null } = $props();

  // Post-share-by-category slices (coloured by volume rank; the legend carries the
  // engagement index). Long tail rolls into "Other" inside the donut.
  const CAT_PALETTE = [
    'var(--color-primary)', 'var(--color-secondary)', 'var(--color-info)',
    'var(--color-success)', 'var(--color-warning)', 'var(--color-violet)',
  ];
  const idxTone = (i: number | null): string =>
    i == null ? 'text-base-content/50' : i >= 1.2 ? 'text-success' : i <= 0.8 ? 'text-warning' : 'text-base-content/68';
  const catSegments = $derived<DonutSeg[]>(
    labels.items.map((l, i) => ({
      label: l.name || `#${l.label_id.slice(0, 6)}`,
      value: l.posts,
      color: CAT_PALETTE[i % CAT_PALETTE.length]!,
      sub: l.engagement_index == null ? '—' : `×${l.engagement_index.toFixed(2)}`,
      subClass: idxTone(l.engagement_index),
    })),
  );

  // Volume-vs-engagement correlation → a plain one-word verdict + its explainer.
  function overwhelmLabel(r: number | null | undefined): string {
    if (r == null) return '—';
    if (r <= -0.3) return 'Overwhelmed';
    if (r >= 0.3) return 'Hungry';
    return 'Balanced';
  }
  function overwhelmTone(r: number | null | undefined): string {
    if (r != null && r <= -0.3) return 'bg-error/70';
    if (r != null && r >= 0.3) return 'bg-success/70';
    return 'bg-base-content/25';
  }
  // Member-only volume vs engagement (moderators excluded). The lever is getting
  // MEMBERS to post more — peer content is what drives engagement, not owner/mod posts.
  function corrText(r: number | null | undefined): string {
    if (r == null) return 'not enough weeks to measure yet';
    if (r <= -0.3) return 'members post faster than the group can engage — spotlight the best so good posts aren’t buried';
    if (r >= 0.3) return 'the more members post, the more everyone engages — prompt more member posts (weekly questions, wins, intros)';
    return 'member volume and engagement move together — keep the rhythm';
  }
</script>

<div class="grid gap-6 lg:grid-cols-2">
  <section class="rounded-box border hair bg-base-100 p-5">
    <div class="eyebrow mb-4">Posts by category · engagement index{#if windowLabel} · {windowLabel}{/if}</div>
    {#if labels.items.length === 0}
      <p class="text-sm text-base-content/62">No labelled posts yet.</p>
    {:else}
      <SegmentDonut segments={catSegments} center="posts" unit="posts" top={6} />
    {/if}
  </section>

  <section class="rounded-box border hair bg-base-100 p-5">
    <div class="eyebrow mb-4">What lands</div>
    <div class="space-y-4">
      <div class="flex items-baseline gap-3">
        <span class="w-2 shrink-0 self-stretch rounded-full bg-success/70"></span>
        <div class="min-w-0">
          <div class="font-display text-lg font-bold tracking-tight">{labels.best ?? '—'}</div>
          <div class="text-xs text-base-content/72">best category · highest engagement per post</div>
        </div>
      </div>
      <div class="flex items-baseline gap-3">
        <span class="w-2 shrink-0 self-stretch rounded-full bg-warning/70"></span>
        <div class="min-w-0">
          <div class="font-display text-lg font-bold tracking-tight">{labels.worst ?? '—'}</div>
          <div class="text-xs text-base-content/72">weakest category · lowest engagement per post</div>
        </div>
      </div>
      <div class="flex items-baseline gap-3">
        <span class="w-2 shrink-0 self-stretch rounded-full {overwhelmTone(timing.overwhelm_corr)}"></span>
        <div class="min-w-0">
          <div class="font-display text-lg font-bold tracking-tight">{overwhelmLabel(timing.overwhelm_corr)}</div>
          <div class="text-xs text-base-content/72">
            Flooding Score · {corrText(timing.overwhelm_corr)}{timing.overwhelm_corr == null ? '' : ` · r ${timing.overwhelm_corr.toFixed(2)}`}
          </div>
        </div>
      </div>
    </div>
  </section>
</div>
